export const samplePlan = {
  id: 'plan-bc-2026',
  name: 'Breast Cancer Trial Participation Plan',
  createdBy: 'Patient (AI-assisted Wizard)',
  managedBy: 'Clinical Trial Management Team',
  approvalStatus: 'In Collaborative Review',
  participants: [
    'Patient',
    'Clinical Trial Management Team',
    'Pharma Company / Trial Sponsor',
    'Resource Providers / Community Health Partners'
  ]
};

export const samplePlanItems = [
  {
    id: 'goal-1',
    type: 'goal',
    name: 'Complete trial participation readiness',
    createdBy: 'Patient (AI-assisted Wizard)',
    managedBy: 'Clinical Trial Management Team',
    approvalStatus: 'Approved',
    parentType: 'plan',
    parentId: 'plan-bc-2026',
    participants: ['Patient', 'Clinical Trial Management Team'],
    details: {
      focusArea: 'Patient-need-centric participation planning across clinical and life support dimensions',
      targetDate: '2026-04-20'
    }
  },
  {
    id: 'objective-clinical-1',
    type: 'objective',
    name: 'Clinical readiness objective: complete screening and baseline tests',
    createdBy: 'Patient + Clinical Team',
    managedBy: 'Clinical Trial Management Team',
    approvalStatus: 'In Collaborative Review',
    parentType: 'goal',
    parentId: 'goal-1',
    participants: ['Patient', 'Clinical Trial Management Team', 'Nurse Navigator'],
    details: {
      objectiveCategory: 'clinical',
      measures: ['Records completed', 'Labs completed', 'Consent packet completed']
    }
  },
  {
    id: 'objective-lifestyle-1',
    type: 'objective',
    name: 'Lifestyle support objective: stabilize transport, nutrition, and daily routine',
    createdBy: 'Patient + Community Partner',
    managedBy: 'Clinical Trial Management Team',
    approvalStatus: 'In Collaborative Review',
    parentType: 'goal',
    parentId: 'goal-1',
    participants: ['Patient', 'Community Health Partner', 'Social Worker'],
    details: {
      objectiveCategory: 'lifestyle',
      measures: ['Transportation reliability', 'Nutrition support engagement', 'Caregiver schedule alignment']
    }
  },
  {
    id: 'activity-1',
    type: 'activity',
    name: 'Community partner activity: schedule transportation and support services',
    createdBy: 'Community Health Partner',
    managedBy: 'Clinical Trial Management Team',
    approvalStatus: 'Proposed',
    parentType: 'objective',
    parentId: 'objective-lifestyle-1',
    participants: ['Patient', 'Community Health Partner', 'Transportation Coordinator'],
    details: {
      postedBy: 'Community Health Partner',
      schedule: [
        'Week 1: confirm ride eligibility',
        'Week 2: publish appointment transport calendar',
        'Week 3: verify contingency transportation support'
      ]
    }
  },
  {
    id: 'intervention-1',
    type: 'intervention',
    name: 'Intervention: symptom monitoring and coaching pathway',
    createdBy: 'Clinical Trial Management Team',
    managedBy: 'Clinical Trial Management Team',
    approvalStatus: 'In Collaborative Review',
    parentType: 'plan',
    parentId: 'plan-bc-2026',
    participants: ['Patient', 'Nurse Navigator', 'Trial Coordinator', 'Community Health Partner'],
    details: {
      linkedObjectiveIds: ['objective-clinical-1', 'objective-lifestyle-1'],
      interventionProtocol: 'Weekly symptom check-ins with escalation and navigation support.'
    }
  }
];

export const aiDraftMessages = [
  'Draft: Please confirm your availability for onboarding labs and baseline screening.',
  'Draft: We recommend aligning transportation support one week before your first trial visit.',
  'Draft: Sponsor team asks to verify consent packet completion before activation.'
];

const PLAN_STORAGE_KEY = 'mammotrials_trial_plan_bundles_v1';

const sampleBundle = {
  plan: samplePlan,
  items: samplePlanItems
};

export const loadPlanBundles = () => {
  try {
    const raw = localStorage.getItem(PLAN_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed;
  } catch (error) {
    console.error('Failed to load plan bundles from storage:', error);
    return [];
  }
};

export const savePlanBundles = (bundles) => {
  try {
    localStorage.setItem(PLAN_STORAGE_KEY, JSON.stringify(bundles));
  } catch (error) {
    console.error('Failed to save plan bundles to storage:', error);
  }
};

export const upsertPlanBundle = ({ plan, items }) => {
  const bundles = loadPlanBundles();
  const filtered = bundles.filter((bundle) => bundle?.plan?.id !== plan.id);
  filtered.push({ plan, items });
  savePlanBundles(filtered);
};

export const deletePlanBundle = (planId) => {
  const bundles = loadPlanBundles();
  const filtered = bundles.filter((bundle) => bundle?.plan?.id !== planId);
  savePlanBundles(filtered);
};

export const listPlans = ({ includeSample = true } = {}) => {
  const bundles = loadPlanBundles();
  const planMap = new Map();

  bundles.forEach((bundle) => {
    if (bundle?.plan?.id) {
      planMap.set(bundle.plan.id, bundle.plan);
    }
  });

  if (includeSample && !planMap.has(samplePlan.id)) {
    planMap.set(samplePlan.id, samplePlan);
  }

  return Array.from(planMap.values());
};

export const getPlanBundle = (planId) => {
  const bundles = loadPlanBundles();
  const found = bundles.find((bundle) => bundle?.plan?.id === planId);
  if (found) {
    return found;
  }
  if (!planId || planId === samplePlan.id) {
    return sampleBundle;
  }
  return null;
};
